import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import AppLayout from '../components/AppLayout';
import './Orders.css';

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function copyToClipboard(text) {
  try { navigator.clipboard.writeText(text); } catch (_) {}
}

function Countdown({ deliverAt }) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    const update = () => {
      const diff = new Date(deliverAt) - Date.now();
      setRemaining(diff > 0 ? diff : 0);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [deliverAt]);

  if (remaining === null) return null;
  if (remaining <= 0) return (
    <span className="countdown countdown-imminent">
      <i className="fa-solid fa-truck-fast" /> Livraison imminente...
    </span>
  );
  const m = Math.floor(remaining / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  return (
    <span className="countdown">
      <i className="fa-solid fa-hourglass-half" /> Livraison dans {m}:{String(s).padStart(2, '0')}
    </span>
  );
}

export default function Orders() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const fetchOrders = useCallback(() => {
    axios.get('/api/shop/orders', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setOrders(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    fetchOrders();
    // Auto-refresh toutes les 15s pour détecter les livraisons
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleCopyCode = (code, orderId) => {
    copyToClipboard(code);
    setCopied(orderId);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <AppLayout>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/shop')}>
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div>
          <h1 className="page-title">Mes commandes</h1>
          <p className="page-subtitle">Retrouvez vos commandes et codes de retrait FastBox</p>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-muted)', padding: '40px 0' }}>
          <i className="fa-solid fa-spinner spinner" style={{ color: 'var(--primary)', fontSize: 20 }} />
          Chargement...
        </div>
      ) : orders.length === 0 ? (
        <div className="orders-empty">
          <div className="orders-empty-icon">📦</div>
          <h3>Aucune commande</h3>
          <p>Vous n'avez pas encore passé de commande.</p>
          <button className="btn-primary" style={{ marginTop: 20 }} onClick={() => navigate('/shop')}>
            <i className="fa-solid fa-store" /> Aller à la boutique
          </button>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map(order => (
            <div key={order.id} className="order-card">
              {/* Header */}
              <div className="order-header">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="order-id">#{order.id.split('-')[0].toUpperCase()}</span>
                  <span className="order-date">
                    <i className="fa-solid fa-clock" />
                    {formatDate(order.created_at)}
                  </span>
                </div>
                <span className={`order-status status-${order.status}`}>
                  {order.status === 'delivered' ? (
                    <><i className="fa-solid fa-check" /> Livré</>
                  ) : (
                    <><i className="fa-solid fa-clock" /> En préparation</>
                  )}
                </span>
                <span className="order-total">{formatAmount(order.total)}</span>
              </div>

              {/* Body */}
              <div className="order-body">
                {/* Items */}
                <div className="order-items-list">
                  <div className="order-items-title">
                    <i className="fa-solid fa-box" /> Contenu de la commande
                  </div>
                  {order.items.map((item, idx) => (
                    <div key={idx} className="order-item-row">
                      <span className="order-item-row-name">{item.productName || `Produit #${item.productId}`}</span>
                      <span className="order-item-row-qty">×{item.quantity || 1}</span>
                      <span className="order-item-row-price">{formatAmount((item.price || 0) * (item.quantity || 1))}</span>
                    </div>
                  ))}
                </div>

                {/* Delivery */}
                {order.status === 'pending' && order.deliver_at && (
                  <div className="delivery-box delivery-box-pending">
                    <div className="delivery-box-title">
                      <i className="fa-solid fa-box-open" /> Commande en préparation
                    </div>
                    <div style={{ marginTop: 8, fontSize: 14 }}>
                      <Countdown deliverAt={order.deliver_at} />
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                      <i className="fa-brands fa-discord" /> Vous recevrez un DM Discord à la livraison
                    </p>
                  </div>
                )}

                {order.status === 'delivered' && order.box_street && order.code && (
                  <div className="delivery-box">
                    <div className="delivery-box-title">
                      <i className="fa-solid fa-map-pin" /> Livraison FastBox
                    </div>
                    <div className="delivery-detail">
                      <span className="delivery-detail-label">FastBox</span>
                      <span className="delivery-detail-value">{order.box_street}</span>
                    </div>
                    <div className="delivery-detail">
                      <span className="delivery-detail-label">Code de retrait</span>
                      <div
                        className="delivery-code-display"
                        title="Cliquer pour copier"
                        onClick={() => handleCopyCode(order.code, order.id)}
                      >
                        {order.code}
                      </div>
                      {copied === order.id && (
                        <span style={{ fontSize: 11, color: 'var(--success)', marginTop: 4, textAlign: 'center' }}>
                          <i className="fa-solid fa-check" /> Copié !
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
